import {
  Download,
  FileText,
  Image,
  Layers,
  MapPin,
  Spline,
  Square,
  Table2,
} from "lucide-react";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/components/theme-provider";
import { cn } from "@/lib/utils";
import type { CatalogueCardEntry, CatalogueDataType } from "content/catalogue";
import { TruncatedText } from "components/shared/truncated-text";
import dayjs from "dayjs";
import { Link, useHistory } from "react-router-dom";
import * as Routes from "routes";
import { productCodeToExplorerLayerIdMappings } from "../constants";
import { viewDatasetInExplorer } from "./explorer-navigation";
import { ComingSoonOverlay } from "./coming-soon";

interface CatalogueCardProps {
  entry: CatalogueCardEntry;
  /** From the datasets API, so absent until that query resolves. */
  updatedDate?: string;
}

export const CatalogueCard = ({ entry, updatedDate }: CatalogueCardProps) => {
  const history = useHistory();
  const { resolvedTheme } = useTheme();
  const image = resolvedTheme === "dark" ? entry.imageDark : entry.imageLight;
  const hasExplorerLayer = !!productCodeToExplorerLayerIdMappings.get(
    entry.datasetProductCode,
  );

  return (
    <Card className="overflow-hidden p-0 gap-0" data-testid="catalogue-item">
      <div className="flex flex-col md:flex-row md:items-stretch flex-1">
        <div className="relative w-full md:w-60 md:min-w-40 shrink-0 overflow-hidden">
          <img
            src={image}
            alt={entry.displayName}
            loading="lazy"
            className={cn(
              "h-full w-full object-cover",
              entry.comingSoon && "scale-110 blur-sm grayscale",
            )}
          />
          {entry.comingSoon ? (
            <ComingSoonOverlay />
          ) : (
            <Badge className="absolute top-2 left-2 gap-1.5">
              {getFeatureIcon(entry.metadata.dataType[0])}
              {Intl.NumberFormat("en", { notation: "compact" }).format(
                entry.metadata.totalFeatureCount,
              )}{" "}
              {humanizeFeatures(entry.metadata.dataType[0])}
            </Badge>
          )}
        </div>

        <div className="flex flex-col flex-1 min-w-0 py-4 gap-4">
          <CardHeader>
            <CardTitle className="min-w-0">
              <TruncatedText className="max-w-full">
                {entry.displayName}
              </TruncatedText>
            </CardTitle>
            {/* Always render the line so the header stays the same height and
                the description below it aligns across cards, even before the
                datasets API returns a date or for coming-soon packs. */}
            <CardDescription className={cn(!updatedDate && "invisible")}>
              {updatedDate
                ? `Updated: ${dayjs(updatedDate).format("MMM YYYY")}`
                : "Updated"}
            </CardDescription>
            <CardAction>
              <div className="flex gap-1">
                {entry.metadata.dataType.map((type) => (
                  <Badge key={type} variant="outline">
                    {type}
                  </Badge>
                ))}
              </div>
            </CardAction>
          </CardHeader>

          <CardContent>
            <p className="line-clamp-4 text-sm text-muted-foreground">
              {entry.shortDescription}
            </p>
          </CardContent>

          <CardFooter className="flex gap-2 mt-auto">
            <Button className="flex-1" asChild>
              <Link to={`${Routes.dataCatalogue}/${entry.slug}`}>
                <FileText />
                Details
              </Link>
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                history.push(
                  `${Routes.dataDownload}?product=${entry.datasetProductCode}`,
                )
              }
            >
              <Download />
              Downloads
            </Button>
            <Button
              variant="outline"
              disabled={!hasExplorerLayer}
              onClick={() =>
                viewDatasetInExplorer(entry.datasetProductCode, history)
              }
            >
              <Layers />
              Explorer
            </Button>
          </CardFooter>
        </div>
      </div>
    </Card>
  );
};

export const getFeatureIcon = (dataType: CatalogueDataType) => {
  const className = "size-3";
  switch (dataType) {
    case "Points":
      return <MapPin className={className} />;
    case "Polygons":
      return <Square className={className} />;
    case "Lines":
      return <Spline className={className} />;
    case "Raster":
      return <Image className={className} />;
    case "Aspatial":
      return <Table2 className={className} />;
    default:
      return <MapPin className={className} />;
  }
};

export const humanizeFeatures = (dataType: CatalogueDataType) => {
  switch (dataType) {
    case "Raster":
      return "Sq KM";
    case "Aspatial":
      return "Rows";
    default:
      return "Features";
  }
};
